import type { ReactNode, CSSProperties } from "react";
import type { CmsBackground } from "@/lib/api/cms-windows";
import { backgroundToStyle } from "./bgStyle";
import styles from "./WDefault.module.css";

/**
 * WDefault — domyślny kontener dla okien (`w_*`).
 *
 * **Rola:** opakowanie wizualne instancji okna — `<section>` z opcjonalnym
 * `<h3>` (nazwa instancji okna z `imp_cms_window.name`) i ramką/tłem.
 * Reuse'owalny przez dispatch lambda w [variants.tsx](./variants.tsx) — każdy
 * variant okna decyduje czy wybiera `WDefault` (h3+ramka), [WEmpty](./WEmpty.tsx)
 * (passthrough), czy inny wrapper. Dawniej tę rolę pełnił `KDefault` w
 * `WindowContainer.tsx` (warstwa drop'nięta).
 *
 * **Różnica vs `CDefault`:** okno to sekcja UI (`<h3>`, mniejszy rozmiar
 * typograficzny, sekcja w grid'cie strony), a katalog to główny content strony
 * (`<h1>`, page title).
 *
 * **3 opcje konfiguracji** (props):
 *   - **nagłówek** (`title`) — `imp_cms_window.name` renderowany jako `<h3>`
 *     (sekcyjny header okna; admin-controlled inline HTML)
 *   - **treść** (`children`) — content variant'a okna (`<WMenu>`, ...)
 *   - **tło** (`background`) — `CmsBackground` aplikowany jako inline style
 *
 * **Komponent zawartości NIE wie że jest w `WDefault`** — variant lambda w
 * `variants.tsx` wprost wrappuje.
 */
export function WDefault({ title, background, children }: WDefaultProps) {
  const bgStyle: CSSProperties | undefined = (() => {
    const s = backgroundToStyle(background);
    return Object.keys(s).length > 0 ? s : undefined;
  })();

  return (
    <section className={styles.root} style={bgStyle}>
      {title && (
        <h3
          className={styles.title}
          dangerouslySetInnerHTML={{ __html: title }}
        />
      )}
      <div className={styles.body}>{children}</div>
    </section>
  );
}

export type WDefaultProps = {
  /** Nazwa instancji okna z `imp_cms_window.name`. Renderowana jako `<h3>`.
   *  Brak/pusty -> bez nagłówka. */
  title?: string;
  /** Tło z `imp_cms_window.config[background]` przez `m_site_backgrounds`. */
  background?: CmsBackground | null;
  children: ReactNode;
};
